"use client";

/**
 * Tracks the gap between the layout viewport bottom and the visual
 * viewport bottom, exposed as the `--vv-bottom-offset` CSS variable on
 * `<html>`. Fixed bottom-nav / footer components read this var to stay
 * pinned to the visible bottom on Chrome Android, where `position:
 * fixed; bottom: 0` aligns to the LAYOUT viewport (which extends behind
 * the URL bar) instead of the visual viewport.
 *
 * Without this hook, on Chrome Android with the URL bar visible the
 * fixed nav appears "suspended" above the bottom of the screen — half
 * hidden behind the URL bar. With it, the nav slides up by exactly the
 * URL bar height when the bar is visible, and locks to `bottom: 0`
 * once the URL bar collapses on scroll.
 *
 * Safe-area-inset-bottom (iPhone home indicator + iOS Safari bottom URL
 * bar) is handled separately via `pb-[max(1rem,env(safe-area-inset-bottom))]`
 * on the nav itself — the two mechanisms compose without overlap.
 *
 * Two ways to mount:
 *   - `<VisualViewportBottomProbe />` — drop-in component, mount once at
 *     app root. Module is `"use client"` so it works inside Next.js
 *     Server Component layouts.
 *   - `useVisualViewportBottomVar()` — call from any existing client
 *     component already mounted at root (avoids an extra component).
 */
import { useEffect } from "react";

const CSS_VAR = "--vv-bottom-offset";

export function useVisualViewportBottomVar(): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) {
      // Older browsers without VisualViewport API — leave the var unset
      // so `bottom: var(--vv-bottom-offset, 0px)` falls back to 0.
      return;
    }

    // Standalone PWA mode (added to home screen, opened from launcher,
    // iOS "open in app"): there is no browser URL bar to dodge, and on
    // iOS the visualViewport already excludes the home-indicator strip —
    // so the offset we'd compute below equals the safe-area-inset-bottom
    // (~34px), which is already owned by component-level
    // `pb-[max(env(safe-area-inset-bottom),...)]`. Applying it again here
    // double-buffers the home indicator: the bottom nav floats 34px above
    // the screen edge AND reserves another 34px of pb. Skip the probe in
    // standalone — safe-area-inset-bottom handles the home indicator on
    // its own.
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      // iOS Safari pre-display-mode media query
      (window.navigator as { standalone?: boolean }).standalone === true;
    if (isStandalone) return;

    const root = document.documentElement;

    const update = (): void => {
      // Distance the visual viewport bottom is shifted UP from the
      // layout viewport bottom. Equals the URL bar height on Chrome
      // Android when the bar is visible; equals 0 when the bar is
      // collapsed (full-screen scroll mode) or on desktop.
      const offset = window.innerHeight - vv.height - vv.offsetTop;
      const clamped = Math.max(0, Math.round(offset));
      root.style.setProperty(CSS_VAR, `${clamped}px`);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    // Some browsers fire orientationchange before VisualViewport
    // dimensions update — re-run on the next frame to catch up.
    const onOrientation = () => requestAnimationFrame(update);
    window.addEventListener("orientationchange", onOrientation);

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("orientationchange", onOrientation);
      root.style.removeProperty(CSS_VAR);
    };
  }, []);
}

/**
 * Drop-in component variant — mount once at the app root. Identical
 * effect to calling `useVisualViewportBottomVar()` from a client
 * component you already own; this just saves you from creating one.
 */
export function VisualViewportBottomProbe(): null {
  useVisualViewportBottomVar();
  return null;
}
